import { setError, startSpinner, stopSpinner } from '@actions/common/actionCreators';
import ENROLLMENT_ACTION_TYPES from '@constants/actionTypes/enrollmentsActionTypes';
import endpoints from '@constants/configs/endpoints';
import { REINSTATEMENT_STATUS } from '@constants/enrollments/enrollments.constants';
import { getDuplicateMemberStrings } from '@modules/members/enrollments/common/enrollmentUtils';
import { IEnrollmentRequest } from '@modules/members/enrollments/common/types';
import API from '@services/API';
import { logError } from '../../utils/tools/ErrorLoggers';
import { setStoredEncHouseholdId, setUserConfidentiality, syncHouseholdReferenceId } from '../SideNav/sideNavActionCreators';

export const setEnrollmentList = data => ({
    type: ENROLLMENT_ACTION_TYPES.SET_ENROLLMENT_LIST,
    payload: data,
});

export const setEnrollmentAndEnrolleeDetails = data => ({
    type: ENROLLMENT_ACTION_TYPES.SET_ENROLLMENTANDENROLLEE_DETAILS,
    payload: data,
});

export const setEnrollmentDetails = data => ({
    type: ENROLLMENT_ACTION_TYPES.SET_ENROLLMENT_DETAILS,
    payload: data,
});

export const setEnrollmentPermissionDetails = data => ({
    type: ENROLLMENT_ACTION_TYPES.SET_PERMISSION_DETAILS,
    payload: data,
});

export const setSelectedEnrollment = data => ({
    type: ENROLLMENT_ACTION_TYPES.SET_SELECTED_ENROLLMENT,
    payload: data,
});

export const setRequestResponse = (data: IEnrollmentRequest) => ({
    type: ENROLLMENT_ACTION_TYPES.SET_REQUEST_RESPONSE,
    payload: data,
});

const actions = { setError, startSpinner, stopSpinner };

export const fetchEnrollmentDetails = async (dispatch, payload) => {
    const params = [
        endpoints.MEMBER_ENROLLMENT_LIST_DETAILS,
        payload,
        'POST',
        true,
        undefined,
        'application/x-www-form-urlencoded',
    ];
    const data = global.__ENV__.PRODUCTION
        ? await API.postData(params, dispatch, actions, true)
        : await API.getData(endpoints.MEMBER_ENROLLMENT_LIST_DETAILS, dispatch, actions, true);
    if (data) {
        return data;
    } else {
        console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_LIST_DETAILS);
    }
};

export const updateEnrollmentDetails = async (dispatch, payload) => {
    const params = [
        endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_UPDATE_ENROLLMENT,
        payload,
        'POST',
        true,
        undefined,
        'application/json;charset=UTF-8',
    ];
    const data = await API.postData(params, dispatch, actions, true);
    if (data) {
        return data;
    } else {
        console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_LIST_DETAILS);
    }
};

export const fetchEnrollmentSnapshot = async (dispatch, payload) => {
    const { enrollmentId, subscriberEventId } = payload;

    const params = [
        endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_DETAIL_SNAPSHOT.processReplacements({
            enrollmentId,
            subscriberEventId,
        }),
        undefined,
        'POST',
        true,
        undefined,
    ];
    const data = global.__ENV__.PRODUCTION
        ? await API.postData(params, dispatch, actions, true)
        : await API.getData(endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_DETAIL_SNAPSHOT, dispatch, actions, true);
    if (data) {
        return data;
    } else {
        console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_DETAIL_SNAPSHOT);
    }
};

export const setMemberEnrollmentResend = async (dispatch, payload) => {
    try {
        const { encEnrollmentId, eventType, comment } = payload;
        const encodedComment = encodeURIComponent(comment);

        const params = [
            endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_RESEND_COMMENT.processReplacements({
                encEnrollmentId,
                eventType,
                comment: encodedComment,
            }),
            undefined,
            'POST',
            true,
            undefined,
        ];
        const data = await API.postData(params, dispatch, actions, true);
        if (data?.status) {
            dispatch(setRequestResponse({ isSuccess: true }));
            return data;
        } else {
            dispatch(setRequestResponse({ isError: true }));
            console.error(
                'Cannot get current user details',
                endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_RESEND_COMMENT.processReplacements({
                    encEnrollmentId,
                    eventType,
                    comment,
                }),
            );
        }
    } catch (error) {
        console.error(error);
    }
};

interface IMember {
    houseHoldCaseId: string;
    firstName: string;
    lastName: string;
    healthEnrollmentId?: string;
    dentalEnrollmentId?: string;
}

export const setUpdateEnrollmentStatus = async (url, dispatch, payload) => {
    try {
        const params = [url, payload, 'POST', true, undefined, undefined, true, true];
        const data = await API.postData(params, dispatch, actions, true);
        if (data?.status) {
            dispatch(setRequestResponse({ isSuccess: true }));
            return data;
        } else {
            const errorMessage = data?.duplicateMemberMap
                ? getDuplicateMemberStrings(data.duplicateMemberMap).join('\n')
                : '';

            dispatch(setRequestResponse({ isError: true, errorMessage: errorMessage }));
            console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_UPDATE_STATUS);
        }
    } catch (error) {
        console.error(error);
    }
};

export const setReinstateEnrollmentStatus = async (dispatch, payload) => {
    try {
        const params = [endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_REINSTATE_STATUS, payload, 'POST', true, undefined];
        const data = await API.postData(params, dispatch, actions, true);
        if (data?.status) {
            if (
                data.status === REINSTATEMENT_STATUS.CANNOT_REINSTATED ||
                data.status === REINSTATEMENT_STATUS.SUBSEQUENT_APP_SUBMITTED
            ) {
                dispatch(setRequestResponse({ isError: true, data: data }));
            } else {
                dispatch(setRequestResponse({ isSuccess: true }));
            }
        } else {
            dispatch(setRequestResponse({ isError: true }));
            console.error(
                'Cannot get current user details',
                endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_REINSTATE_STATUS,
            );
        }
    } catch (error) {
        console.error(error);
    }
};

export const setCancelEnrollment = async (dispatch, payload) => {
    try {
        const params = [endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_CANCEL_ACTIVE, payload, 'POST', true, undefined];
        const data = await API.postData(params, dispatch, actions, true);
        if (data?.status === 'SUCCESS') {
            dispatch(setRequestResponse({ isSuccess: true }));
            return data;
        } else {
            dispatch(setRequestResponse({ isError: true }));
            console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_LIST_ENROLLMENT_CANCEL_ACTIVE);
        }
    } catch (error) {
        console.error(error);
    }
};

export const fetchEnrollmentAndEnrolleeDetails = payload => async dispatch => {
    const params = [endpoints.MEMBER_ENROLLMENT_LIST_ENROLLEE_DETAILS, payload, 'POST', true, undefined];
    const data = global.__ENV__.PRODUCTION
        ? await API.postData(params, dispatch, actions, true)
        : await API.getData(endpoints.MEMBER_ENROLLMENT_LIST_ENROLLEE_DETAILS, dispatch, actions, true);
    if (data) {
        dispatch(setEnrollmentAndEnrolleeDetails({ ...data }));
    } else {
        console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_LIST_ENROLLEE_DETAILS);
    }
    return data;
};

export const fetchPermissionDetails = (id, showSpinner = false) => async dispatch => {
    try {
        const data = await API.getData(
            endpoints.MEMBER_ENROLLMENT_LIST_PERMISSIONS.processReplacements({ id }),
            dispatch,
            actions,
            showSpinner,
        );
        if (data && data.data) {
            dispatch(setEnrollmentPermissionDetails(data.data));
            if (data.data.household && typeof data.data.household === 'object') {
                const isConfidential = data.data.household.confidential === true;
                dispatch(setUserConfidentiality(isConfidential));
                dispatch(syncHouseholdReferenceId(data.data.household));
            } else {
                logError('fetchPermissionDetails: household data missing or invalid');
                dispatch(setUserConfidentiality(false));
            }
            // Seed encHouseHoldId for other SPA consumers (e.g. getEncHouseholdId).
            // Not a SideNav guard — those key off Redux so reloads refetch.
            setStoredEncHouseholdId(id);
            // Return the loaded record so callers can confirm the refetch for
            // this specific household succeeded (the store slice is shared and
            // not keyed to a household, so a truthy result is the only reliable
            // signal that the slice now reflects the requested id).
            return data.data;
        } else {
            logError('Cannot get permission details', endpoints.MEMBER_ENROLLMENT_LIST_PERMISSIONS);
            dispatch(setUserConfidentiality(false));
            dispatch(setError('Failed to load permission details'));
        }
    } catch (error) {
        logError('fetchPermissionDetails error:', error);
        dispatch(setUserConfidentiality(false));
        dispatch(setError('Failed to load permission details'));
    }
    return undefined;
};

export const fetchEnrollmentPremiumDetails = async (id, dispatch) => {
    try {
        const response = await API.getData(
            endpoints.MEMBER_ENROLLMENT_DETAILS.processReplacements({ id }),
            dispatch,
            actions,
            false,
        );
        if (response) {
            return response?.data;
        } else {
            console.error('Cannot get current user details', endpoints.MEMBER_ENROLLMENT_DETAILS);
        }
    } catch (error) {
        console.log(error);
    }
};
